import React from "react";
import { useTranslation } from "react-i18next";
import {
  Document,
  folder as mockFolder,
  Id,
  Identifiable,
} from "@planzone/dto";
import { Modal } from "@common/modal";
import { Button } from "@common/button";
import {
  useAttachDocumentsToElement,
  useRetrieveDocuments,
} from "@planzone/view-models";
import { useMessage } from "@components/useMessage";
import { Props } from "./types";
import { FilesSystem } from "./filesSystem";
import { SelectDocumentsContext, getSelectedFolderPath } from "./context";
import { getSelectedFolder } from "./filesSystem/utils/getSelectedFolder";
import styles from "./styles.scss";

function AttachDocuments({
  project,
  selectDocuments,
  elementId,
  elementType,
  cancel,
}: Props) {
  const { t } = useTranslation();
  const { isRequestPending, isRequestFailure, attachDocumentsToElement } =
    useAttachDocumentsToElement(elementId);
  const {
    isRequestPending: isRetrieveDocumentsRequestPending,
    isRequestSuccess: isRetrieveDocumentsRequestSuccess,
    isRequestFailure: isRetrieveDocumentsRequestFailure,
    folder: rootFolder,
    retrieveDocuments,
  } = useRetrieveDocuments(project.uuid, "project");
  const [currentFolderId, setCurrentFolderId] = React.useState<string>("");
  const [selectedDocuments, setSelectedDocuments] = React.useState<
    Identifiable<Document>[]
  >([]);
  const { path } = React.useMemo(
    () =>
      getSelectedFolderPath(
        { uuid: currentFolderId, ...mockFolder },
        rootFolder,
      ),
    [currentFolderId],
  );
  const currentFolder = React.useMemo(
    () => getSelectedFolder(rootFolder, currentFolderId) || rootFolder,
    [rootFolder, currentFolderId],
  );
  const addToSelection = (document: Identifiable<Document>) =>
    setSelectedDocuments((prevState) => [...prevState, document]);

  const removeFromSelection = (documentUuid: Id) => {
    const documentIndex = selectedDocuments.findIndex(
      (doc) => doc.uuid === documentUuid,
    );

    setSelectedDocuments((prevState) => [
      ...prevState.slice(0, documentIndex),
      ...prevState.slice(documentIndex + 1),
    ]);
  };

  const resetSelection = () => setSelectedDocuments(() => []);
  const setCurrentFolder = (folderId: Id) => setCurrentFolderId(folderId);

  React.useEffect(() => {
    !isRetrieveDocumentsRequestPending &&
      !isRetrieveDocumentsRequestSuccess &&
      !isRetrieveDocumentsRequestFailure &&
      retrieveDocuments({ namespace: project.uuid, type: "project" });
  }, [project.uuid]);

  React.useEffect(() => {
    if (currentFolderId === "" && rootFolder) {
      setCurrentFolderId(rootFolder.uuid);
    }
  }, [rootFolder, currentFolderId]);

  useMessage(
    (event) =>
      event.type === "uploadDocumentSucceeded" &&
      attachDocumentsToElement({
        projectId: project.uuid,
        elementType,
        elementId,
        documents: [event.data.document],
      }),
    { localOnly: true },
  );

  return (
    <SelectDocumentsContext.Provider
      value={{
        loading: isRequestPending,
        project,
        selection: selectedDocuments,
        currentFolderId,
        currentFolderPath: path,
        currentFolder,
        rootFolder,
        setCurrentFolder,
        removeFromSelection,
        addToSelection,
        resetSelection,
      }}
    >
      <Modal
        header={t("selectDocuments.title")}
        className={styles.attachDocuments}
        close={cancel}
      >
        <FilesSystem />
        <div className={styles.buttons}>
          <Button type="cancel" press={cancel}>
            {t("selectDocuments.cancel")}
          </Button>
          <Button
            type="submit"
            status={
              isRequestPending
                ? "pending"
                : isRequestFailure
                  ? "failure"
                  : "none"
            }
            isDisabled={isRequestPending}
            press={() => {
              if (elementType === "EXPENSE" && selectDocuments) {
                selectDocuments(selectedDocuments);
                cancel();
              } else {
                attachDocumentsToElement({
                  projectId: project.uuid,
                  elementType,
                  elementId,
                  documents: selectedDocuments,
                });
              }
            }}
          >
            {t("selectDocuments.submit")}
          </Button>
        </div>
      </Modal>
    </SelectDocumentsContext.Provider>
  );
}
export { AttachDocuments };
